/* eslint-disable react/jsx-no-useless-fragment */

import React, { useState } from "react";

import { useLazyQuery } from "@apollo/client";
import { Col, Row } from "react-bootstrap";

import CustomHelmet from "components/calculations/components/CustomHelmet";
import Button from "components/common/Button";
import Loading from "components/common/Loading";
import Modal from "components/common/Modal";
import { DocumentPreview } from "components/documents/DocumentPreview";
import PrecedentsTree from "components/precedentsTree/PrecedentsTree";

import useModal from "hooks/useModal";

import { extractSectionHtmlById } from "helpers/extractSectionHtmlById";
import { titleCase } from "helpers/titleCase";
import { truncateText } from "helpers/truncateText";

import TEMPLATE_WITH_SECTIONS from "@gql/sanity/templateWithSections";

import { decompressDescription } from "../../../utils/descriptionCompression";

export const DOCUMENT_SECTIONS_MODAL_NAME = "CLIENT_DOCUMENT_SECTIONS";

const convertTreeToNestedFormat = (tree) => {
  function processNodes(nodes) {
    if (!nodes) return [];

    return nodes.flatMap((node) => [
      { section: node.id, hidden: !node.checked },
      ...processNodes(node.nodes),
    ]);
  }

  return processNodes(tree.nodes);
};

const generateTree = (args) => {
  const { sections = [], config = {} } = args;

  const defaultCheckedFields = config.defaultSectionsState || {};

  const sortedSections = [...sections].sort((a, b) => {
    const aParts = a.section.split(".").map((part) => parseInt(part, 10) || 0);
    const bParts = b.section.split(".").map((part) => parseInt(part, 10) || 0);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;

      if (aPart !== bPart) {
        return aPart - bPart;
      }
    }
    return 0;
  });

  const parseSectionName = (bookmarkSection) => {
    const { label, section, tag, hidden } = bookmarkSection;

    const numbers = section.split(".").map((s) => Number.parseInt(s, 10));

    return {
      section,
      label,
      numbers,
      tag,
      hidden,
    };
  };

  const parsedSections = sortedSections.map((bn) => parseSectionName(bn));

  /* ----------------- Group schedules ---------------- */
  const isSchedule = (sec) => (sec.numbers[0] ?? 0) > 17;

  const regularSections = parsedSections.filter((s) => !isSchedule(s));
  const scheduleSections = parsedSections.filter(isSchedule);

  // local generator using existing logic
  const buildTreeNodes = (sectionsArray) => {
    const gen = (level, current) => {
      const filtered = sectionsArray.filter(
        (b) =>
          b.numbers.length === level + 1 && current.every((num, idx) => b.numbers[idx] === num),
      );

      if (!filtered.length) return null;

      const isRoot = level === 0;

      return filtered.map((sectionInfo) => {
        const { label, numbers, section, hidden } = sectionInfo;

        const childNodes = gen(level + 1, numbers);

        const safeLabel = typeof label === "string" ? titleCase(label) : "";
        const title = `${section}${isRoot ? "." : ""} ${safeLabel}`;

        return {
          title,
          checked: Object.hasOwn(defaultCheckedFields, label)
            ? defaultCheckedFields[label]
            : !hidden,
          key: `${section}-${label}`,
          id: section,
          nestingLevel: level,
          nodes: childNodes,
          className: isRoot ? "parent-node" : undefined,
          label,
        };
      });
    };

    return gen(0, []) || [];
  };

  const regularNodes = buildTreeNodes(regularSections);

  const scheduleChildNodes = buildTreeNodes(scheduleSections).map((n) => ({
    ...n,
    title: typeof n.label === "string" ? titleCase(n.label) : "", // remove section number
    nestingLevel: 1,
    className: undefined,
    key: `${n.id}-${n.label}`, // Ensure unique key
  }));

  if (scheduleChildNodes.length) {
    regularNodes.push({
      title: "Schedules and Certificates",
      checked: scheduleChildNodes.every((n) => n.checked),
      key: "SchedulesAndCertificates",
      id: "SchedulesAndCertificates",
      nestingLevel: 0,
      nodes: scheduleChildNodes,
      className: "parent-node",
    });
  }

  const nodes = regularNodes;

  return {
    nodes,
  };
};

const ClientDocumentsSectionsModal = () => {
  const { options, close } = useModal(DOCUMENT_SECTIONS_MODAL_NAME);

  const { document, sections, normalizedPaths, submit: createDocument, client } = options || {};
  const [tree, setTree] = useState([]);
  const [node, setNode] = useState(null);
  const [html, setHtml] = useState("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Generate HTML for preview, applying fallback only for schedule sections (18+)
  const previewHtml = React.useMemo(() => {
    if (!node) return "";

    const sectionHtml = extractSectionHtmlById(html, node.section);

    // Determine if current node represents a schedule (section 18+)
    const firstNumber = Number.parseInt(node.section.split(".")[0], 10) || 0;
    const isScheduleSection = firstNumber > 17;

    if (isScheduleSection) {
      const labelPresent = sectionHtml?.toLowerCase()?.includes((node.label || "").toLowerCase());

      if (!sectionHtml?.trim() || !labelPresent) {
        return '<p style="text-align:center;padding:1rem">No preview available for this schedule</p>';
      }
    }

    return truncateText(sectionHtml);
  }, [html, node]);

  const [getDescriptionById] = useLazyQuery(TEMPLATE_WITH_SECTIONS, {
    fetchPolicy: "cache-first",
  });

  React.useEffect(() => {
    if (sections) {
      setTree(
        generateTree({
          sections: sections || [],
          config: {
            defaultSectionsState: {
              "Remote Signing": !!client?.remoteSigningEnabled,
            },
          },
        }),
      );
      setNode(sections[0]);
    }
  }, [sections, client?.remoteSigningEnabled]);

  React.useEffect(() => {
    const fetchTemplateDescription = async () => {
      if (document?._id) {
        setIsLoadingTemplate(true);
        try {
          const { data } = await getDescriptionById({
            variables: { id: document._id },
          });

          const rawDescription = data?.allDocumentTemplate?.[0]?.description || "";
          setHtml(decompressDescription(rawDescription));
        } catch (error) {
          console.error("[ERROR LOADING TEMPLATE DESCRIPTION]:", error);
          setHtml("");
        } finally {
          setIsLoadingTemplate(false);
        }
      }
    };

    fetchTemplateDescription();
  }, [document?._id, getDescriptionById]);

  const onChange = (key, val) => {
    // Renamed from `setNode` to `updateNodeSelection` to avoid shadowing the outer state setter `setNode`
    const updateNodeSelection = (currentNode) => {
      const updatedNode = { ...currentNode };
      const isExactMatch = currentNode.id === key;
      if (isExactMatch) {
        updatedNode.checked = val;
        const propagate = (nd) => {
          const updated = { ...nd, checked: val };
          if (updated.nodes) {
            updated.nodes = updated.nodes.map(propagate);
          }
          return updated;
        };
        if (updatedNode.nodes) {
          updatedNode.nodes = updatedNode.nodes.map(propagate);
        }
      } else if (updatedNode.nodes) {
        updatedNode.nodes = updatedNode.nodes.map(updateNodeSelection);
      }
      if (!isExactMatch && updatedNode.nodes) {
        updatedNode.checked = updatedNode.nodes.some((n) => n.checked);
      }
      return updatedNode;
    };

    const updateParentNodeState = (node) => {
      if (node.nodes) {
        const updatedNodes = node.nodes.map(updateParentNodeState);
        const isAnyChildChecked = updatedNodes.some((n) => n.checked);
        node.checked = isAnyChildChecked;
        node.nodes = updatedNodes;
      }
      return node;
    };

    const updatedTree = updateNodeSelection(tree);
    setTree(updateParentNodeState(updatedTree));
  };

  const handleSubmit = () => {
    const sections = convertTreeToNestedFormat(tree);

    createDocument(sections);

    close();
  };

  const handleSetNode = (id) => {
    if (node.section === id) return null;

    setNode(sections.find((section) => section.section === id));
  };

  return (
    <Modal
      size="md"
      name={DOCUMENT_SECTIONS_MODAL_NAME}
      title="Select Sections"
      noFooter
      showCloseButton
      className="client-document-sections-form"
    >
      <CustomHelmet
        title="Document Sections"
        description="Document Sections"
        href="https://www.divorcepath.com/app/[new-document]"
        isCanonical
      />
      <React.Fragment>
        <Row>
          <Col sm={5}>
            <div className="sections-modal-content left">
              <PrecedentsTree
                tree={tree}
                selectedNode={node}
                onCheckboxChange={onChange}
                onNodeSelect={handleSetNode}
              />
            </div>
          </Col>
          <Col sm={7}>
            <div className="sections-modal-content right">
              <div className="section-description-content">
                <div className="section-title">{node?.label}</div>
                {isLoadingTemplate ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "300px",
                      width: "100%",
                    }}
                  >
                    <Loading />
                  </div>
                ) : (
                  <DocumentPreview html={previewHtml} />
                )}
              </div>
            </div>
          </Col>
          <div className="sections-modal-footer">
            <div style={{ marginLeft: "20px" }}>
              <strong>* Check schedules to include</strong>
            </div>
            <div className="sections-modal-footer-buttons">
              <Button size="sm" onClick={handleSubmit} className="btn-submit">
                Confirm
              </Button>
              <Button size="sm" variant="secondary" onClick={close} className="btn-cancel">
                Cancel
              </Button>
            </div>
          </div>
        </Row>
      </React.Fragment>
    </Modal>
  );
};

export default ClientDocumentsSectionsModal;
